import { KatAlert, KatButton, KatDropdown, KatInput, KatSpinner, KatTab, KatTabs } from '@amzn/katal-react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { SITE_ID_PARAM_NAME } from 'src/core/config/navigationRoutes';
import { useFCConfig } from 'src/features/hooks/useFCConfig';
import { usePCDetails } from 'src/features/hooks/usePCDetails';
import { useUser } from 'src/utils/UserContext';
import { updateFCConfig, triggerCategorizationGeneration, updatePCOverrides, deletePCOverride } from 'src/utils/Queries';
import { ProcessPathMappingEntry } from 'src/utils/Queries';
import 'src/features/styles/shared/colors.css';
import './FCConfigPage.css';

interface PCColumnProps {
    pcName: string;
    ppList: ProcessPathMappingEntry[];
    editMode?: boolean;
    isNew?: boolean;
    overrides?: Record<string, string>;
    pendingRemovals?: string[];
    onDrop?: ( ppName: string, targetPC: string ) => void;
    onRemoveOverride?: ( ppName: string ) => void;
}

const PCColumn: React.FC<PCColumnProps> = ({ pcName, ppList, editMode, isNew, overrides, pendingRemovals, onDrop, onRemoveOverride }) => {
    const [dragOver, setDragOver] = useState(false);
    const sorted = useMemo(() => [...ppList].sort((a, b) => a.processPathName.localeCompare(b.processPathName)), [ppList]);

    const handleDragOver = useCallback(( e: React.DragEvent ) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOver(false);
    }, []);

    const handleDrop = useCallback(( e: React.DragEvent ) => {
        e.preventDefault();
        setDragOver(false);
        const ppName = e.dataTransfer.getData('text/plain');
        if (ppName && onDrop) {
            onDrop(ppName, pcName);
        }
    }, [pcName, onDrop]);

    const handleDragStart = useCallback(( e: React.DragEvent, ppName: string ) => {
        e.dataTransfer.setData('text/plain', ppName);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const isOverridden = ( ppName: string ) => overrides && ppName in overrides;

    return (
        <div
            className={`pc-column ${editMode ? 'pc-column-editable' : ''} ${isNew ? 'pc-column-new' : ''} ${dragOver ? 'pc-column-drag-over' : ''}`}
            onDragOver={editMode ? handleDragOver : undefined}
            onDragLeave={editMode ? handleDragLeave : undefined}
            onDrop={editMode ? handleDrop : undefined}
        >
            <div className="pc-column-header">
                <span className="pc-column-name">{pcName}</span>
                <span className="pc-column-count">{ppList.length}</span>
            </div>
            <div className="pc-column-body">
                {sorted.map(pp => (
                    <div
                        key={pp.processPathName}
                        className={`pp-chip ${pp.isFamilyOverridden ? 'pp-chip-overridden' : ''} ${(isOverridden(pp.processPathName) || pp.isPCOverridden) ? 'pp-chip-pc-overridden' : ''} ${editMode ? 'pp-chip-draggable' : ''} ${pendingRemovals?.includes(pp.processPathName) ? 'pp-chip-pending-removal' : ''}`}
                        draggable={editMode}
                        onDragStart={editMode ? (e) => handleDragStart(e, pp.processPathName) : undefined}
                    >
                        <div className="pp-chip-top">
                            <span className="pp-chip-name">{pp.processPathName}</span>
                            {pp.isFamilyOverridden && (
                                <span className="override-badge" data-tooltip={"Family overridden from " + pp.processPathFamily}>⚠</span>
                            )}
                            {isOverridden(pp.processPathName) && (
                                <span className="override-badge override-badge-pc" data-tooltip="PC Override active">✏️</span>
                            )}
                            {pp.isPCOverridden && !isOverridden(pp.processPathName) && (
                                <span className="override-capsule">Overridden</span>
                            )}
                        </div>
                        <div className="pp-chip-bottom">
                            {editMode && (isOverridden(pp.processPathName) || pp.isPCOverridden) && onRemoveOverride && (
                                <button
                                    className="pp-chip-remove-override"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemoveOverride(pp.processPathName); }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    draggable={false}
                                    title="Remove override — PP returns to system default"
                                >✕</button>
                            )}
                        </div>
                    </div>
                ))}
                {editMode && ppList.length === 0 && (
                    <div className="pc-column-empty-drop">Drop PPs here</div>
                )}
            </div>
        </div>
    );
};

const FCConfigPage: React.FC = () => {
    const { user } = useUser();
    const { [SITE_ID_PARAM_NAME]: siteId } = useParams();
    const { data, isLoading, error, refetch } = useFCConfig(siteId);
    const { data: pcDetails, isLoading: pcDetailsLoading, refetch: refetchPCDetails } = usePCDetails(siteId);

    const [selectedTab, setSelectedTab] = useState('configuration');
    const [selectedMappingType, setSelectedMappingType] = useState<string>('');
    const [selectedGenerationType, setSelectedGenerationType] = useState<string>('');
    const [ticketUrl, setTicketUrl] = useState<string>('');
    const [saveStatus, setSaveStatus] = useState<{ success: boolean; message: string } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);

    // Edit mode state for PC-PP overrides
    const [editMode, setEditMode] = useState(false);
    const [pendingOverrides, setPendingOverrides] = useState<Record<string, string>>({});
    const [newPCName, setNewPCName] = useState('');
    const [showNewPCInput, setShowNewPCInput] = useState(false);
    const [overrideTicketUrl, setOverrideTicketUrl] = useState('');
    const [isSavingOverrides, setIsSavingOverrides] = useState(false);
    const [createdPCs, setCreatedPCs] = useState<string[]>([]);
    const [recentlyRemovedPPs, setRecentlyRemovedPPs] = useState<string[]>([]);

    useEffect(() => {
        if (data) {
            setSelectedMappingType(data.productionCapabilityMappingType || '');
            setSelectedGenerationType(data.categorizationGenerationType || '');
        }
    }, [data]);

    const pcGroupedMappings = useMemo(() => {
        if (!pcDetails?.processPathMappings) return {};
        const grouped: Record<string, ProcessPathMappingEntry[]> = {};
        pcDetails.processPathMappings.forEach(pp => {
            // Hide PPs that were just un-overridden (optimistic removal until pipeline propagates)
            if (recentlyRemovedPPs.includes(pp.processPathName)) return;
            const pc = pp.productionCapability || 'Unknown';
            if (!grouped[pc]) grouped[pc] = [];
            grouped[pc].push(pp);
        });
        return grouped;
    }, [pcDetails, recentlyRemovedPPs]);

    const totalPPs = pcDetails?.processPathMappings?.length || 0;
    const totalPCs = Object.keys(pcGroupedMappings).length;

    // In edit mode, apply pending overrides to the view (preview of what will be saved)
    const editModeGroupedMappings = useMemo(() => {
        const base = { ...pcGroupedMappings };

        // Add user-created empty PCs
        createdPCs.forEach(pc => {
            if (!base[pc]) base[pc] = [];
        });

        if (!editMode || Object.keys(pendingOverrides).length === 0) return base;

        // Deep copy
        const result: Record<string, ProcessPathMappingEntry[]> = {};
        Object.entries(base).forEach(([pc, pps]) => {
            result[pc] = pps.filter(pp => !(pp.processPathName in pendingOverrides));
        });
        // Add overridden PPs to their target PCs
        Object.entries(pendingOverrides).forEach(([ppName, targetPC]) => {
            const originalPP = pcDetails?.processPathMappings?.find(pp => pp.processPathName === ppName);
            if (originalPP) {
                if (!result[targetPC]) result[targetPC] = [];
                result[targetPC].push(originalPP);
            }
        });
        // Remove empty PCs that existed before (not user-created)
        Object.keys(result).forEach(pc => {
            if (result[pc].length === 0 && pcGroupedMappings[pc] && !createdPCs.includes(pc) && !Object.values(pendingOverrides).includes(pc)) {
                delete result[pc];
            }
        });
        return result;
    }, [editMode, pendingOverrides, pcGroupedMappings, pcDetails, createdPCs]);

    const handleDrop = useCallback(( ppName: string, targetPC: string ) => {
        // Find current PC for this PP
        const currentPC = pcDetails?.processPathMappings?.find(pp => pp.processPathName === ppName)?.productionCapability;
        if (currentPC === targetPC) {
            // Dropped in same column — remove override if exists
            const updated = { ...pendingOverrides };
            delete updated[ppName];
            setPendingOverrides(updated);
        } else {
            setPendingOverrides(prev => ({ ...prev, [ppName]: targetPC }));
        }
    }, [pcDetails, pendingOverrides]);

    const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);

    const handleRemoveOverride = useCallback(( ppName: string ) => {
        // If it's a pending (unsaved) move, just remove from pending overrides
        if ( ppName in pendingOverrides ) {
            const updated = { ...pendingOverrides };
            delete updated[ppName];
            setPendingOverrides(updated);
            return;
        }
        // If it's a saved override, mark as pending removal
        if ( !pendingRemovals.includes(ppName) ) {
            setPendingRemovals(prev => [...prev, ppName]);
        }
    }, [pendingOverrides, pendingRemovals]);

    const handleCreateNewPC = useCallback(() => {
        const trimmed = newPCName.trim();
        if (!trimmed) return;
        if (pcGroupedMappings[trimmed] || createdPCs.includes(trimmed)) {
            setSaveStatus({ success: false, message: `PC "${trimmed}" already exists.` });
            return;
        }
        setCreatedPCs(prev => [...prev, trimmed]);
        setShowNewPCInput(false);
        setNewPCName('');
        setSaveStatus({ success: true, message: `PC "${trimmed}" created. Drag process paths into it.` });
        // Scroll to the start so the new PC (first column) is visible
        setTimeout(() => {
            const wrapper = document.querySelector('.pc-columns-wrapper');
            if (wrapper) wrapper.scrollTo({ left: 0, behavior: 'smooth' });
        }, 100);
    }, [newPCName, pcGroupedMappings, createdPCs]);

    const handleSaveOverrides = useCallback(async () => {
        if (!siteId || !user) return;

        // Validate: any created PC must have at least 1 PP dragged into it
        for (const pc of createdPCs) {
            const hasPPs = Object.values(pendingOverrides).includes(pc);
            if (!hasPPs) {
                setSaveStatus({ success: false, message: `PC "${pc}" has no process paths. Drag at least one PP into it or remove it.` });
                return;
            }
        }

        // Must have at least one change (move or removal)
        if (Object.keys(pendingOverrides).length === 0 && pendingRemovals.length === 0) {
            return;
        }

        setIsSavingOverrides(true);
        setSaveStatus(null);
        try {
            // Process pending removals first
            for (const ppName of pendingRemovals) {
                await deletePCOverride({
                    fcName: siteId,
                    processPathName: ppName,
                    ticketUrl: overrideTicketUrl.trim(),
                    updatedBy: user.login || 'unknown',
                });
            }

            // Then process new overrides (if any)
            if (Object.keys(pendingOverrides).length > 0) {
                const response = await updatePCOverrides({
                    fcName: siteId,
                    overrides: pendingOverrides,
                    ticketUrl: overrideTicketUrl.trim(),
                    updatedBy: user.login || 'unknown',
                });
                if (!response.success) {
                    setSaveStatus({ success: false, message: response.message });
                    return;
                }
            }

            const hadRemovals = pendingRemovals.length > 0;
            setSaveStatus({ success: true, message: hadRemovals
                ? 'Changes saved successfully. Categorization regeneration triggered. Removed overrides may take up to 60 seconds to fully propagate — you may see stale data briefly.'
                : 'Changes saved successfully. Categorization regeneration triggered.' });
            setEditMode(false);
            setRecentlyRemovedPPs(prev => [...prev, ...pendingRemovals]);
            setPendingOverrides({});
            setPendingRemovals([]);
            setOverrideTicketUrl('');
            setCreatedPCs([]);
            refetchPCDetails();
            // For removals, poll until CPP-CDS propagates the revert
            if (hadRemovals) {
                let attempts = 0;
                const poll = setInterval(() => {
                    attempts++;
                    refetchPCDetails();
                    if (attempts >= 6) { // 60 seconds max
                        clearInterval(poll);
                        setRecentlyRemovedPPs([]);
                    }
                }, 10000);
            }
        } catch (e: any) {
            setSaveStatus({ success: false, message: e.message });
        } finally {
            setIsSavingOverrides(false);
        }
    }, [siteId, user, pendingOverrides, pendingRemovals, overrideTicketUrl, createdPCs, refetchPCDetails]);

    if (!user?.isDevAdmin) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Access Denied">Developer access required.</KatAlert></div>;
    }

    if (!siteId) {
        return <div className="fc-config-page"><KatAlert variant="info" header="No Site Selected">Provide a site ID in the URL (e.g., /fcconfig/BFI4).</KatAlert></div>;
    }

    if (isLoading) {
        return <div className="fc-config-page"><KatSpinner /></div>;
    }

    if (error) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Error">{error.message}</KatAlert></div>;
    }

    const categorizationType = data?.productionCapabilityMappingType === 'ONE_DC' ? 'HOV' : 'PICKING';
    const hasChanges = selectedMappingType !== (data?.productionCapabilityMappingType || '') ||
                       selectedGenerationType !== (data?.categorizationGenerationType || '');
    const isTicketValid = ticketUrl.trim().startsWith('https://');
    const canSave = hasChanges && isTicketValid && !isSaving;

    const handleSaveAndRegenerate = async () => {
        setIsSaving(true);
        setSaveStatus(null);
        try {
            const response = await updateFCConfig({
                fcName: siteId,
                productionCapabilityMappingType: selectedMappingType,
                categorizationGenerationType: selectedGenerationType,
                ticketUrl: ticketUrl.trim(),
                updatedBy: user?.login || 'unknown',
            });
            setSaveStatus({ success: response?.success ?? false, message: response?.message ?? 'No response' });
            if (response?.success) refetch();
        } catch (e: any) {
            setSaveStatus({ success: false, message: e.message });
        } finally {
            setIsSaving(false);
        }
    };

    const handleRegenerate = async () => {
        setIsRegenerating(true);
        setSaveStatus(null);
        try {
            const response = await triggerCategorizationGeneration(siteId, categorizationType);
            setSaveStatus({ success: response?.success ?? false, message: response?.message ?? 'No response' });
        } catch (e: any) {
            setSaveStatus({ success: false, message: e.message });
        } finally {
            setIsRegenerating(false);
        }
    };

    return (
        <div className="fc-config-page">
            <div className="fc-config-header">
                <h2>{siteId}</h2>
            </div>

            <div className="fc-config-active-config">
                <span className="fc-config-active-label">Active Configuration</span>
                <div className="fc-config-capsules">
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">Mapping</span>
                        <span className="fc-config-capsule-value">{data?.productionCapabilityMappingType}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">Rules</span>
                        <span className="fc-config-capsule-value">{data?.categorizationGenerationType}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">Type</span>
                        <span className="fc-config-capsule-value">{categorizationType}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">PCs</span>
                        <span className="fc-config-capsule-value">{totalPCs}</span>
                    </span>
                    <span className="fc-config-capsule">
                        <span className="fc-config-capsule-label">PPs</span>
                        <span className="fc-config-capsule-value">{totalPPs}</span>
                    </span>
                </div>
            </div>

            {saveStatus && (
                <div className="fc-config-status-container">
                    <KatAlert
                        variant={saveStatus.success ? 'success' : 'danger'}
                        header={saveStatus.success ? 'Success' : 'Failed'}
                    >
                        {saveStatus.message}
                    </KatAlert>
                </div>
            )}

            <KatTabs selected={selectedTab} onChange={(e: any) => setSelectedTab(e.detail.selected)}>
                <KatTab tabId="configuration" label="Configuration" />
                <KatTab tabId="pc-mapping" label="PC-PP Mapping" />
            </KatTabs>

            {selectedTab === 'configuration' && (
                <div className="fc-config-tab-content">
                    <div className="fc-config-form">
                        <div className="fc-config-form-row">
                            <label className="fc-config-form-label">PC Mapping Type</label>
                            <KatDropdown
                                value={selectedMappingType}
                                options={(data?.availableProductionCapabilityMappingTypes || []).map((t: string) => ({ name: t, value: t }))}
                                onChange={(e: any) => setSelectedMappingType(e.detail.value)}
                            />
                        </div>
                        <div className="fc-config-form-row">
                            <label className="fc-config-form-label">Rule Generation Type</label>
                            <KatDropdown
                                value={selectedGenerationType}
                                options={(data?.availableCategorizationGenerationTypes || []).map((t: string) => ({ name: t, value: t }))}
                                onChange={(e: any) => setSelectedGenerationType(e.detail.value)}
                            />
                        </div>
                        <div className="fc-config-form-row">
                            <label className="fc-config-form-label">Ticket / MCM URL</label>
                            <KatInput
                                value={ticketUrl}
                                placeholder="https://..."
                                onChange={(e: any) => setTicketUrl(e.detail.value)}
                            />
                        </div>
                        <div className="fc-config-form-actions">
                            <KatButton
                                variant="primary"
                                onClick={handleSaveAndRegenerate}
                                disabled={!canSave}
                                label={isSaving ? 'Saving...' : 'Save & Regenerate'}
                            />
                            <KatButton
                                variant="secondary"
                                onClick={handleRegenerate}
                                disabled={isRegenerating}
                                label={isRegenerating ? 'Regenerating...' : 'Regenerate Rules Only'}
                            />
                        </div>
                        {!hasChanges && (
                            <p className="fc-config-hint">Change at least one value above to enable saving.</p>
                        )}
                    </div>
                </div>
            )}

            {selectedTab === 'pc-mapping' && (
                <div className="fc-config-tab-content">
                    {pcDetailsLoading ? (
                        <div className="table-loading-container"><KatSpinner /></div>
                    ) : totalPPs === 0 ? (
                        <div className="table-empty-container">
                            <p>No PC-PP mapping data available for this FC.</p>
                        </div>
                    ) : (
                        <div className="pc-mapping-container">
                            <div className="pc-mapping-toolbar">
                                <span>{totalPPs} process paths across {totalPCs} production capabilities</span>
                                <div className="pc-mapping-toolbar-actions">
                                    {!editMode ? (
                                        <KatButton variant="secondary" label="Edit Overrides" onClick={() => { setEditMode(true); setPendingOverrides({}); setSaveStatus(null); }} />
                                    ) : (
                                        <KatButton variant="secondary" label="Cancel" onClick={() => { setEditMode(false); setPendingOverrides({}); setPendingRemovals([]); setShowNewPCInput(false); setCreatedPCs([]); setSaveStatus(null); }} />
                                    )}
                                </div>
                            </div>
                            {editMode && (
                                <div className="pc-mapping-edit-panel">
                                    <div className="pc-mapping-edit-info">
                                        🖱️ <strong>Drag and drop</strong> process path chips between columns to reassign them to a different PC. Changes are previewed live and take effect after you click Save.
                                    </div>
                                    {(Object.keys(pendingOverrides).length > 0 || pendingRemovals.length > 0) && (
                                        <div className="pc-mapping-pending-changes">
                                            <strong>Pending changes ({Object.keys(pendingOverrides).length + pendingRemovals.length}):</strong>
                                            <ul>
                                                {Object.entries(pendingOverrides).map(([pp, pc]) => (
                                                    <li key={pp}>
                                                        {pp} → {pc}
                                                        <button className="pending-change-remove" onClick={() => { const updated = { ...pendingOverrides }; delete updated[pp]; setPendingOverrides(updated); }}>✕</button>
                                                    </li>
                                                ))}
                                                {pendingRemovals.map(pp => (
                                                    <li key={`rm-${pp}`} style={{color: '#d13212'}}>
                                                        {pp} → (remove override)
                                                        <button className="pending-change-remove" onClick={() => setPendingRemovals(prev => prev.filter(p => p !== pp))}>✕</button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="pc-mapping-edit-save-row">
                                        <KatInput
                                            value={overrideTicketUrl}
                                            placeholder="https://sim.amazon.com/issues/..."
                                            onChange={(e: any) => setOverrideTicketUrl(e.detail?.value || e.target?.value || '')}
                                            onInput={(e: any) => setOverrideTicketUrl(e.detail?.value || e.target?.value || '')}
                                        />
                                        <KatButton
                                            variant="primary"
                                            label={isSavingOverrides ? 'Saving...' : 'Save & Refresh Rules'}
                                            disabled={(Object.keys(pendingOverrides).length === 0 && pendingRemovals.length === 0) || !overrideTicketUrl.trim().startsWith('https://') || isSavingOverrides}
                                            onClick={handleSaveOverrides}
                                        />
                                    </div>
                                    <div className="pc-mapping-create-pc">
                                        {!showNewPCInput ? (
                                            <KatButton variant="secondary" label="+ Create New PC" onClick={() => setShowNewPCInput(true)} />
                                        ) : (
                                            <div className="pc-mapping-new-pc-row">
                                                <KatInput value={newPCName} placeholder="PC_NewName" onChange={(e: any) => setNewPCName(e.detail?.value || e.target?.value || '')} onInput={(e: any) => setNewPCName(e.detail?.value || e.target?.value || '')} />
                                                <KatButton variant="primary" label="Add" disabled={!newPCName.trim()} onClick={handleCreateNewPC} />
                                                <KatButton variant="secondary" label="Cancel" onClick={() => { setShowNewPCInput(false); setNewPCName(''); }} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="pc-columns-scroll-hint">← Scroll horizontally to see all PCs →</div>
                            <div className="pc-columns-wrapper">
                                {Object.entries(editModeGroupedMappings)
                                    .sort(([a], [b]) => {
                                        // Newly created PCs always come first
                                        const aIsNew = createdPCs.includes(a);
                                        const bIsNew = createdPCs.includes(b);
                                        if (aIsNew && !bIsNew) return -1;
                                        if (!aIsNew && bIsNew) return 1;
                                        if (aIsNew && bIsNew) return createdPCs.indexOf(a) - createdPCs.indexOf(b);

                                        const order = ['PC_Singles', 'PC_Multis', 'PC_BOD', 'PC_PickToRebin'];
                                        const aIdx = order.indexOf(a);
                                        const bIdx = order.indexOf(b);
                                        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                                        if (aIdx !== -1) return -1;
                                        if (bIdx !== -1) return 1;
                                        if (a === 'PC_Other') return 1;
                                        if (b === 'PC_Other') return -1;
                                        if (a.startsWith('PC_Trans')) return 1;
                                        if (b.startsWith('PC_Trans')) return -1;
                                        return a.localeCompare(b);
                                    })
                                    .map(([pcName, ppList]) => (
                                    <PCColumn
                                        key={pcName}
                                        pcName={pcName}
                                        ppList={ppList}
                                        editMode={editMode}
                                        isNew={createdPCs.includes(pcName)}
                                        overrides={pendingOverrides}
                                        pendingRemovals={pendingRemovals}
                                        onDrop={handleDrop}
                                        onRemoveOverride={handleRemoveOverride}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FCConfigPage;
